import { useCallback, useState } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogOverlay, DialogPortal } from "@/components/ui/dialog"
import { TextArea } from "@/components/ui/input"
import { ListRadioGroup, ListRadioItem } from "@/components/ui/list"
import {
  FeedbackSchema,
  FeedbackSchemaType,
} from "@/modules/patients/detail/conclude/feedback-dialog/schema/feedback-schema"
import { AgentConsultationFeedbackInput } from "@/modules/patients/services/__generated__/useConcludeAppointmentMutation.graphql"
import { ConsultationFeedbackReason } from "@/types/__generated__/union-types"

type AgentFeedbackDialogProps = {
  onSubmit: (feedback: AgentConsultationFeedbackInput) => void
  isLoading?: boolean
}

type LikedOption = "liked" | "disliked"

// Custom order to match the design (not alphabetical from generated types)
const REASON_OPTIONS_ORDER: ConsultationFeedbackReason[] = [
  "AREA_OF_EXPERTISE",
  "LANGUAGE_CONFIDENCE",
  "AGE_BAND",
  "OTHER",
]

const REASON_TRANSLATION_KEYS: Record<ConsultationFeedbackReason, string> = {
  AREA_OF_EXPERTISE: "patient.conclude.feedback.reasons.areaOfExpertise",
  LANGUAGE_CONFIDENCE: "patient.conclude.feedback.reasons.languageConfidence",
  AGE_BAND: "patient.conclude.feedback.reasons.ageBand",
  OTHER: "patient.conclude.feedback.reasons.other",
}

const AgentFeedbackDialog = ({ onSubmit, isLoading }: AgentFeedbackDialogProps) => {
  const { t } = useTranslation()
  const [likedOption, setLikedOption] = useState<LikedOption>("liked")

  const form = useForm<FeedbackSchemaType>({
    mode: "all",
    resolver: zodResolver(FeedbackSchema),
    defaultValues: {
      likedConsultation: true,
      reason: undefined,
      otherReasonText: undefined,
    },
  })

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = form

  const reason = watch("reason")

  const handleLikedOptionChange = useCallback(
    (value: LikedOption) => {
      setLikedOption(value)
      const likedConsultation = value === "liked"
      setValue("likedConsultation", likedConsultation)
      if (likedConsultation) {
        setValue("reason", undefined)
        setValue("otherReasonText", undefined)
      }
    },
    [setValue],
  )

  const handleReasonChange = useCallback(
    (value: ConsultationFeedbackReason) => {
      setValue("reason", value)
      if (value !== "OTHER") {
        setValue("otherReasonText", undefined)
      }
    },
    [setValue],
  )

  const handleFormSubmit = useCallback(
    (data: FeedbackSchemaType) => {
      const feedbackInput: AgentConsultationFeedbackInput = {
        likedConsultation: data.likedConsultation,
        reason: data.likedConsultation ? null : data.reason,
        otherReasonText: data.reason === "OTHER" ? data.otherReasonText : null,
      }
      onSubmit(feedbackInput)
    },
    [onSubmit],
  )

  return (
    <Dialog open onOpenChange={() => {}}>
      <FormProvider {...form}>
        <DialogPortal>
          <DialogOverlay />
          <DialogContent
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            onOpenAutoFocus={(e) => e.preventDefault()}
            variant="small"
            data-testid="feedback-dialog-content">
            <DialogHeader className="px-6 pt-6 pb-2">
              <span className="text-on-surface text-xl leading-7 font-bold" data-testid="feedback-dialog-title">
                {t("patient.conclude.feedback.title")}
              </span>
            </DialogHeader>

            <div className="flex flex-col gap-4 px-6 py-4">
              <p className="text-on-surface-subdued text-sm">{t("patient.conclude.feedback.question")}</p>

              <ListRadioGroup
                className="gap-2"
                value={likedOption}
                onValueChange={(value) => handleLikedOptionChange(value as LikedOption)}
                data-testid="liked-options">
                <ListRadioItem value="liked" className="p-0 py-1" data-testid="feedback-option-liked">
                  <div className="text-on-surface text-sm leading-5">
                    {t("patient.conclude.feedback.options.liked")}
                  </div>
                </ListRadioItem>
                <ListRadioItem value="disliked" className="p-0 py-1" data-testid="feedback-option-disliked">
                  <div className="text-on-surface text-sm leading-5">
                    {t("patient.conclude.feedback.options.disliked")}
                  </div>
                </ListRadioItem>
              </ListRadioGroup>

              {likedOption === "disliked" && (
                <div className="flex flex-col gap-2">
                  <p className="text-on-surface-subdued text-sm">{t("patient.conclude.feedback.reasonLabel")}</p>
                  <Controller
                    name="reason"
                    control={control}
                    render={({ field }) => (
                      <ListRadioGroup
                        className="gap-2"
                        value={field.value ?? ""}
                        onValueChange={(value) => handleReasonChange(value as ConsultationFeedbackReason)}
                        data-testid="reason-options">
                        {REASON_OPTIONS_ORDER.map((reasonValue) => (
                          <ListRadioItem
                            key={reasonValue}
                            value={reasonValue}
                            error={!!errors.reason}
                            className="p-0 py-1"
                            data-testid={`feedback-reason-${reasonValue}`}>
                            <div className="text-on-surface text-sm leading-5">
                              {t(REASON_TRANSLATION_KEYS[reasonValue])}
                            </div>
                          </ListRadioItem>
                        ))}
                      </ListRadioGroup>
                    )}
                  />
                  {errors.reason && (
                    <p className="text-critical text-xs" data-testid="reason-error">
                      {errors.reason.message}
                    </p>
                  )}

                  {reason === "OTHER" && (
                    <Controller
                      name="otherReasonText"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          {...field}
                          label={t("patient.conclude.feedback.otherReasonPlaceholder")}
                          textLimit={100}
                          error={errors.otherReasonText?.message}
                          data-testid="other-reason-textarea"
                        />
                      )}
                    />
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="flex justify-end gap-2 p-6 pt-4">
              <Button
                onClick={handleSubmit(handleFormSubmit)}
                isLoading={isLoading}
                data-testid="feedback-submit-button">
                {t("patient.conclude.feedback.submit")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </FormProvider>
    </Dialog>
  )
}

export { AgentFeedbackDialog }
